import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Outlet, To, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
    Alert, Alignment, Button, ButtonGroup, Divider, Intent, Navbar, OverlayToaster, Position
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import useAuth from '../../aws/useAuth'
import LanguageSelect from './LanguageSelect'
import styles from './Root.module.scss'
import ThemeButton from './ThemeButton'

export default (props: {
  signInLink: (currentSearchParams: URLSearchParams) => To,
  signUpLink: (currentSearchParams: URLSearchParams) => To,
  profilesLink: (currentSearchParams: URLSearchParams) => To,
  cardsLink: (currentSearchParams: URLSearchParams) => To,
  rootLink: (currentSearchParams: URLSearchParams) => To,
}) => {
  const navigate = useNavigate()
  const [searchParams,] = useSearchParams()
  const auth = useAuth()
  const { t } = useTranslation()
  const [toaster, setToaster] = useState<OverlayToaster | null>(null)
  const [isSignOutAlertOpen, setIsSignOutAlertOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)

  useEffect(() => {
    const matchMedia = window.matchMedia('(prefers-color-scheme: dark)')
    setIsDarkMode(matchMedia.matches)
    const handleChange = (e: MediaQueryListEvent) => setIsDarkMode(e.matches)
    matchMedia.addEventListener('change', handleChange)
    return () => matchMedia.removeEventListener('change', handleChange)
  }, [])

  return <>
    <Helmet
      title={t('root.header.title')}
    />

    <OverlayToaster
      className={styles.toaster}
      position={Position.BOTTOM}
      ref={setToaster} />

    <div className={styles.root}>
      <Navbar
        fixedToTop
      >
        <Navbar.Group align={Alignment.LEFT}>
          <Navbar.Heading>
            <img
              src={`${process.env.PUBLIC_URL}/images/logo-${isDarkMode ? 'dark' : 'light'}.png`}
              className={styles.headerLogo}
              onClick={() => navigate(props.rootLink(searchParams))}
              alt="shuriken"
            />
          </Navbar.Heading>
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          {auth.isAuthenticated ? <>
            <Button
              className={styles.logoutButton}
              text={t('root.navbar.signOutButton.label')}
              icon={IconNames.LogOut}
              intent={Intent.DANGER}
              onClick={() => setIsSignOutAlertOpen(true)}
              minimal />
          </> : <>
            <Button
              text={t("root.navbar.signInButton.label")}
              minimal
              onClick={() => navigate(props.signInLink(searchParams))}
            />
            <Button
              text={t("root.navbar.signUpButton.label")}
              minimal
              onClick={() => navigate(props.signUpLink(searchParams))}
            />
          </>}
          <ThemeButton minimal hidden />
          <LanguageSelect minimal hidden />
        </Navbar.Group>
      </Navbar>
      <div className={styles.navbarSpacer}></div>

      {auth.isAuthenticated && <>
        <ButtonGroup
          className={styles.topButtonGroup}
          minimal>
          <Button
            text={t('root.profilesButton.label')}
            icon={IconNames.People}
            onClick={() => navigate(props.profilesLink(searchParams))} />
          <Divider />
          <Button
            text={t('root.cardsButton.label')}
            icon={IconNames.CreditCard}
            onClick={() => navigate(props.cardsLink(searchParams))} />
        </ButtonGroup>
        <Divider />
      </>}

      <div className={styles.content}>
        <Outlet context={{ toaster }} />
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <span className={`bp5-ui-text bp5-text-small bp5-text-muted ${styles.copyrightLabel}`}>
          {t('root.footer.copyright.label')}
        </span>
      </div>

      <Alert
        className={styles.alert}
        intent={Intent.WARNING}
        isOpen={isSignOutAlertOpen}
        canOutsideClickCancel
        canEscapeKeyCancel
        confirmButtonText={t('root.signOutAlert.confirmButton.label')}
        cancelButtonText={t('root.signOutAlert.cancelButton.label')}
        onCancel={() => setIsSignOutAlertOpen(false)}
        onConfirm={() => {
          setIsSignOutAlertOpen(false)
          auth.signOut()
        }}>
        <p>{t('root.signOutAlert.message')}</p>
      </Alert>
    </div>
  </>
}

type ContextType = { toaster: OverlayToaster | null }
export const useRootOutletContext = () => useOutletContext<ContextType>()
